import { getApiUrl } from "../api";
import { useEffect, useRef, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import {
  consumeAuthReturnPath,
  getAccountDashboardPath,
  navigateToAuthDestination,
  storeAuthResult,
} from "../auth";

const UAE_PASS_STATE_KEY = "tarahumUaePassState";
const UAE_PASS_NONCE_KEY = "tarahumUaePassNonce";
const UAE_PASS_RETURN_KEY = "tarahumUaePassReturnTo";
const UAE_PASS_RESTRICTED_KEY = "tarahumUaePassRestricted";
const UAE_PASS_RESTRICTED_PROFILE_KEY = "tarahumUaePassRestrictedProfile";
const UAE_PASS_REGISTRATION_PROFILE_KEY = "tarahumUaePassRegistrationProfile";
const UAE_PASS_PROCESSED_CODE_KEY = "tarahumUaePassProcessedCode";
const UAE_PASS_LOGIN_CANCELLED_MESSAGE = "User cancelled the login.";
const UAE_PASS_LOGIN_CANCELLED_MESSAGE_AR = "قام المستخدم بإلغاء تسجيل الدخول";
const UAE_PASS_INACTIVE_CODE_MESSAGE =
  "This UAE PASS login request has expired or has already been used. Please return to the login page and try again.";
const UAE_PASS_LOGIN_CANCELLATION_ERRORS = new Set([
  "invalid_request",
  "login_required",
  "access_denied",
  "cancelledonapp",
]);

const isUaePassLoginCancellation = (error, description) => {
  const normalizedError = String(error || "").trim().toLowerCase();
  const normalizedDescription = String(description || "").trim().toLowerCase();

  return (
    UAE_PASS_LOGIN_CANCELLATION_ERRORS.has(normalizedError) ||
    /\bcancel(?:led|ed)\b/.test(normalizedDescription)
  );
};

const isInactiveAuthorizationCodeError = (result) => {
  const errorText = [
    result?.errorCode,
    result?.error,
    result?.message,
    result?.detail,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    errorText.includes("invalid_grant") ||
    errorText.includes("inactive authorization code")
  );
};

const getStoredRestrictedProfile = () => {
  try {
    const value = window.sessionStorage.getItem(UAE_PASS_RESTRICTED_PROFILE_KEY);
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
};

const formatProfileValue = (value) => {
  if (value === null || value === undefined || value === "") return "Not provided";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};


const firstProfileValue = (profile, keys) => {
  const profileKeys = Object.keys(profile || {});
  for (const key of keys) {
    const matchingKey = profileKeys.find(
      (profileKey) => profileKey.toLowerCase() === key.toLowerCase()
    );
    const value = matchingKey ? profile[matchingKey] : undefined;
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return "";
};

export default function UaePassCallback() {
  const interactions = useHomeInteractions();
  const hasRun = useRef(false);
  const hasFreshOAuthResponse = useRef((() => {
    const params = new URLSearchParams(window.location.search);
    return params.has("code") || params.has("error");
  })());
  const restrictedOnLoad = useRef(
    !hasFreshOAuthResponse.current &&
      window.sessionStorage.getItem(UAE_PASS_RESTRICTED_KEY) === "1"
  );
  const [status, setStatus] = useState("Completing UAE PASS login...");
  const [isError, setIsError] = useState(restrictedOnLoad.current);
  const [errorType, setErrorType] = useState(
    restrictedOnLoad.current ? "existing-users-only" : ""
  );
  const [restrictedProfile, setRestrictedProfile] = useState(getStoredRestrictedProfile);
  const [pendingLogin, setPendingLogin] = useState(null);
  const [isProceeding, setIsProceeding] = useState(false);

  useEffect(() => {
    document.title = "UAE PASS Login | Tarahum Charity Foundation";
  }, []);

  useEffect(() => {
    if (restrictedOnLoad.current) return;
    if (hasRun.current) return;
    hasRun.current = true;

    const finishLogin = async () => {
      const params = new URLSearchParams(window.location.search);
      const providerError = params.get("error");
      const providerDescription = params.get("error_description");
      const code = params.get("code");
      const state = params.get("state");
      const expectedState = window.sessionStorage.getItem(UAE_PASS_STATE_KEY);
      const showRestrictedPage = (message) => {
        window.sessionStorage.setItem(UAE_PASS_RESTRICTED_KEY, "1");
        window.sessionStorage.removeItem(UAE_PASS_RESTRICTED_PROFILE_KEY);
        setRestrictedProfile(null);
        setErrorType("existing-users-only");
        setIsError(true);
        setStatus(message || "This service is only for registered users.");
      };
      const showCallbackError = (message, type = "callback-error") => {
        window.sessionStorage.removeItem(UAE_PASS_RESTRICTED_KEY);
        window.sessionStorage.removeItem(UAE_PASS_RESTRICTED_PROFILE_KEY);
        setErrorType(type);
        setIsError(true);
        setStatus(message || "Unable to complete UAE PASS login.");
      };
      try {
        window.sessionStorage.removeItem(UAE_PASS_RESTRICTED_KEY);
        window.sessionStorage.removeItem(UAE_PASS_RESTRICTED_PROFILE_KEY);

        if (providerError) {
          if (isUaePassLoginCancellation(providerError, providerDescription)) {
            showCallbackError(UAE_PASS_LOGIN_CANCELLED_MESSAGE, "login-cancelled");
            return;
          }
          throw new Error(providerDescription || providerError);
        }

        if (!code) {
          throw new Error("UAE PASS did not return an authorization code.");
        }

        if (expectedState && state !== expectedState) {
          throw new Error("UAE PASS state validation failed. Please try again.");
        }

        if (window.sessionStorage.getItem(UAE_PASS_PROCESSED_CODE_KEY) === code) {
          showCallbackError(
            UAE_PASS_INACTIVE_CODE_MESSAGE,
            "authorization-code-inactive"
          );
          return;
        }
        window.sessionStorage.setItem(UAE_PASS_PROCESSED_CODE_KEY, code);

        const endpoint = process.env.REACT_APP_UAE_PASS_EXCHANGE_ENDPOINT || "/api/UaePassTokenExchange.ashx";
        const redirectUri = window.location.hostname.toLowerCase() === "tarahum.ae"
          ? `${window.location.origin}/uae-pass-callback`
          : (process.env.REACT_APP_UAE_PASS_REDIRECT_URI || `${window.location.origin}${window.location.pathname}`);
        const response = await fetch(getApiUrl(endpoint), {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          credentials: "include",
          body: JSON.stringify({ code, state, redirectUri }),
        });

        const result = await response.json().catch(() => ({}));
        if (response.ok && result.success && result.registrationRequired) {
          window.sessionStorage.setItem(
            UAE_PASS_REGISTRATION_PROFILE_KEY,
            JSON.stringify(result.profile || {})
          );
          window.sessionStorage.removeItem(UAE_PASS_STATE_KEY);
          window.sessionStorage.removeItem(UAE_PASS_NONCE_KEY);
          setStatus("UAE PASS verified. Redirecting to registration...");
          navigateToAuthDestination("/registration");
          return;
        }

        if (!response.ok || !result.success) {
          if (result.errorCode === "existing_users_only") {
            showRestrictedPage(result.message || result.detail);
          } else if (isInactiveAuthorizationCodeError(result)) {
            showCallbackError(
              UAE_PASS_INACTIVE_CODE_MESSAGE,
              "authorization-code-inactive"
            );
          } else {
            showCallbackError(result.detail || result.message);
          }
          return;
        }

        window.sessionStorage.removeItem(UAE_PASS_RESTRICTED_KEY);
        window.sessionStorage.removeItem(UAE_PASS_RESTRICTED_PROFILE_KEY);
        setPendingLogin(result);
        setStatus("Please review your UAE PASS details before continuing.");
      } catch (error) {
        showCallbackError(error.message);
      }
    };

    finishLogin();
  }, []);

  const profile = pendingLogin?.profile || {};
  const clientDetails = pendingLogin ? [
    {
      label: "Full name",
      value: firstProfileValue(profile, ["fullnameEN", "fullNameEN", "name"]) || pendingLogin.userName,
    },
    {
      label: "Arabic name",
      value: firstProfileValue(profile, ["fullnameAR", "fullNameAR"]),
    },
    {
      label: "Email address",
      value: firstProfileValue(profile, ["email", "emailAddress"]) || pendingLogin.user,
    },
    {
      label: "Mobile number",
      value: firstProfileValue(profile, ["mobile", "phone", "phoneNumber"]),
    },
    {
      label: "Emirates ID Number",
      value: firstProfileValue(profile, [
        "idn",
        "emiratesId",
        "emiratesID",
        "emiratesIdNumber",
        "eid",
        "eidNumber",
      ]) || "Not provided by UAE PASS",
      alwaysShow: true,
    },
    {
      label: "Nationality",
      value: firstProfileValue(profile, ["nationalityEN", "nationality", "nationalityAR"]),
    },
  ].filter((detail) => detail.value || detail.alwaysShow) : [];

  const handleProceed = async () => {
    if (!pendingLogin || isProceeding) return;

    setIsProceeding(true);
    try {
      const completedLogin = pendingLogin;

      storeAuthResult(completedLogin, true, "uae-pass");
      window.sessionStorage.removeItem(UAE_PASS_STATE_KEY);
      window.sessionStorage.removeItem(UAE_PASS_NONCE_KEY);

      const returnTo = window.sessionStorage.getItem(UAE_PASS_RETURN_KEY) ||
        consumeAuthReturnPath(
          completedLogin.redirectTo ||
          getAccountDashboardPath(completedLogin.profile?.accounttype)
        );
      window.sessionStorage.removeItem(UAE_PASS_RETURN_KEY);
      window.sessionStorage.removeItem("tarahumAuthReturnTo");
      setStatus("Login successful. Redirecting...");
      navigateToAuthDestination(returnTo);
    } catch (error) {
      setStatus(error.message || "Unable to complete UAE PASS login.");
      setIsProceeding(false);
    }
  };

  const restrictedDetails = Object.entries(restrictedProfile || {})
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => ({
      key,
      value: formatProfileValue(value),
    }));
  const isRestrictedError = errorType === "existing-users-only";
  const isLoginCancelled = errorType === "login-cancelled";

  if (isError) {
    return (
      <>
        <Preloader />
        <Header />
        <LanguagePopup />

        <main className="uae-pass-restricted-page">
          <section className="uae-pass-restricted-card" aria-labelledby="accessRestrictedTitle"> 
            <div className="uae-pass-restricted-icon" aria-hidden="true">
              <i className="fa-solid fa-lock"></i>
              <span>!</span>
            </div>

            <div className="uae-pass-restricted-ar" lang="ar" dir="rtl">
              <h1>
                {isRestrictedError
                  ? "غير مؤهل للوصول إلى الخدمة"
                  : "تعذر إكمال تسجيل الدخول عبر الهوية الرقمية"}
              </h1>
              {isRestrictedError ? (
                <p>
                أنت غير مؤهل للوصول إلى هذه الخدمة. إما أن حسابك لم تتم ترقيته أو أن لديك حساب زائر. يُرجى التواصل مع مؤسسة تراحم لتمكين من الوصول إلى الخدمة             
                </p>
              ) : (
                <p>
                  {isLoginCancelled
                    ? UAE_PASS_LOGIN_CANCELLED_MESSAGE_AR
                    : "يرجى العودة إلى صفحة تسجيل الدخول والمحاولة مرة أخرى"}
                </p>
              )}
            </div>

            <div className="uae-pass-restricted-divider"></div>

            <div className="uae-pass-restricted-en">
              <h2 id="accessRestrictedTitle">
                {isRestrictedError ? "Access Restricted" : "UAE PASS Login Failed"}
              </h2>
              {isRestrictedError ? (
                <p>
                  You are not eligible to access this service. Your account is either not
                  upgraded or you have a visitor account. Please contact Tarahum Charity
                  Foundation to access the services.
                </p>
              ) : (
                <p>{status}</p>
              )}
            </div>

            {!isRestrictedError && restrictedDetails.length ? (
              <div className="uae-pass-received-profile">
                <h3>UAE PASS Details Received</h3>
                <dl>
                  {restrictedDetails.map((detail) => (
                    <div className="uae-pass-received-profile-row" key={detail.key}>
                      <dt>{detail.key}</dt>
                      <dd>{detail.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : null}

            <div className="uae-pass-restricted-actions">
              <Link
                to={isRestrictedError ? "/contact-us" : "/login"}
                className="uae-pass-restricted-btn secondary"
              >
                <i className={`fa-solid ${isRestrictedError ? "fa-phone" : "fa-arrow-left"}`}></i>
                <span>
                  <small lang="ar" dir="rtl">
                    {isRestrictedError ? "تواصل معنا" : "العودة إلى تسجيل الدخول"}
                  </small>
                  {isRestrictedError ? "Contact Us" : "Back to Login"}
                </span>
              </Link>
              <Link to="/" className="uae-pass-restricted-btn primary">
                <i className="fa-solid fa-house"></i>
                <span>
                  <small lang="ar" dir="rtl">العودة إلى الرئيسية</small>
                  Back to Home
                </span>
              </Link>
            </div>
          </section>
        </main>

        <Footer />
        <FloatingWidgets {...interactions} />
        <HomeModals {...interactions} />
      </>
    );
  }

  return (
    <>
      <Preloader />
      <Header />
      <LanguagePopup />

      <section className="login-mdn">
        <div className="container">
          <div className="login-mdn-card uae-pass-callback-card">
            <div className="login-mdn-left">
              <div className="login-mdn-brand">
                <div className="login-mdn-secure">UAE PASS</div>
              </div>
              <div className="login-mdn-mini">
                <div className="login-mdn-mini-box">
                  <strong>{isError ? "!" : "..."}</strong>
                  <span>{isError ? "Login needs attention" : "Secure identity verification in progress"}</span>
                </div>
              </div>
            </div>

            <div className="login-mdn-right">
              <div className="login-mdn-form">
                <div className="login-mdn-title-badge">
                  <span></span>
                  UAE PASS
                </div>
                <h2>
                  {isError
                    ? "Login could not be completed"
                    : pendingLogin
                      ? "Confirm Your Details"
                      : "Completing Login"}
                </h2>
                <p className="desc">{status}</p>
                {pendingLogin ? (
                  <>
                    <dl className="uae-pass-client-details">
                      {clientDetails.map((detail) => (
                        <div className="uae-pass-client-detail" key={detail.label}>
                          <dt>{detail.label}</dt>
                          <dd>{detail.value}</dd>
                        </div>
                      ))}
                    </dl>
                    <button
                      type="button"
                      className="login-mdn-submit uae-pass-proceed-btn"
                      onClick={handleProceed}
                      disabled={isProceeding}
                    >
                      {isProceeding ? "Signing in..." : "Proceed"}
                    </button>
                  </>
                ) : null}
                {isError ? (
                  <Link to="/login" className="login-mdn-submit uae-pass-return-link">
                    Back to Login
                  </Link>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
